import { SearchContent } from '@/components/Search/SearchContent';
import { SearchControls } from '@/components/Search/SearchControls';
import {
  getExtensionsQueryParameter,
  getFoldersQueryParameter,
  getMetadataFiltersQueryParameter,
} from '@/components/Search/utils';
import { useCrossTenantGeometricSearch } from '@/hooks/api/crossTenant/assetsGeometricSearch/useCrossTenantGeometricSearch';
import { useCrossTenantPartSearch } from '@/hooks/api/crossTenant/assetsPartSearch/useCrossTenantPartSearch';
import { useCrossTenantVisualSearch } from '@/hooks/api/crossTenant/assetsVisualSearch/useCrossTenantVisualSearch';
import { useCrossTenantMetadataSearch } from '@/hooks/api/crossTenant/metadataSearch/useCrossTenantMetadataSearch';
import { useTextSearch } from '@/hooks/api/crossTenant/textsearch/useTextSearch';
import { useVisualSearch } from '@/hooks/api/crossTenant/visualSearch/useVisualSearch';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { createUseStyles } from '@/services/createUseStyles';
import { useFileUpload } from '@/services/fileUpload';
import { useTenants } from '@/services/tenants/useTenants';
import { Box } from '@mui/material';
import { useEffect } from 'react';

const useStyles = createUseStyles({
  SearchLayout_root: {
    flex: 1,
    minWidth: '400px',
    height: '100%',
    display: 'flex',
  },
});

export const SearchPage = () => {
  const c = useStyles();
  const { fileUpload, setFileUpload } = useFileUpload();
  const { getTenantId } = useTenants();
  const currentTenantId = getTenantId();
  const { isMetadataSearchEnabled } = useFeatureFlags();

  const {
    searchQuery,
    selectedFilters,
    assetId,
    searchType,
    isTextSearch,
    isImageSearch,
    minThreshold,
    sizeThreshold,
    selectedTenantId: searchTenantId,
  } = useSearchFilters();

  useEffect(() => {
    if (searchType !== 'image') {
      setFileUpload({ files: undefined, useCase: undefined });
    }
  }, [searchType]);

  const { isLoading: textSearchLoading } = useTextSearch({
    searchQuery: searchQuery ?? '',
    selectedFilters,
    enabled: isTextSearch && !!searchTenantId,
    tenantId: searchTenantId,
  });

  const { isLoading: visualSearchLoading } = useVisualSearch({
    files: fileUpload.files,
    searchQuery: searchQuery ?? '',
    metadataFilters: getMetadataFiltersQueryParameter(selectedFilters),
    folders: getFoldersQueryParameter(selectedFilters),
    extensions: getExtensionsQueryParameter(selectedFilters),
    enabled: isImageSearch,
    tenantId: searchTenantId,
  });

  const { isLoading: assetGeometricSearchLoading } = useCrossTenantGeometricSearch({
    assetId,
    assetTenantId: currentTenantId,
    searchTenantId,
    searchQuery,
    minThreshold,
    selectedFilters,
    enabled: searchType === 'geometric' && !!currentTenantId,
  });

  const { isLoading: assetPartSearchLoading } = useCrossTenantPartSearch({
    assetId,
    assetTenantId: currentTenantId,
    searchTenantId,
    searchQuery,
    minThreshold,
    selectedFilters,
    enabled: searchType === 'part' && !!currentTenantId,
  });

  const { isLoading: assetVisualSearchLoading } = useCrossTenantVisualSearch({
    assetId,
    assetTenantId: currentTenantId,
    searchTenantId,
    searchQuery: searchQuery ?? '',
    selectedFilters,
    sizeThreshold,
    enabled: searchType === 'visual' && !!currentTenantId,
  });

  const { isLoading: assetMetadataSearchLoading } = useCrossTenantMetadataSearch({
    assetId,
    assetTenantId: currentTenantId,
    searchTenantId,
    searchQuery: searchQuery ?? '',
    selectedFilters,
    enabled: isMetadataSearchEnabled && searchType === 'metadata' && !!currentTenantId,
  });

  let isLoading: boolean;
  switch (searchType) {
    case 'image': {
      isLoading = visualSearchLoading;
      break;
    }
    case 'geometric': {
      isLoading = assetGeometricSearchLoading;
      break;
    }
    case 'part': {
      isLoading = assetPartSearchLoading;
      break;
    }
    case 'visual': {
      isLoading = assetVisualSearchLoading;
      break;
    }
    case 'metadata': {
      isLoading = assetMetadataSearchLoading;
      break;
    }
    case 'text': {
      isLoading = textSearchLoading;
      break;
    }
    default: {
      isLoading = false;
    }
  }

  return (
    <Box className={c.SearchLayout_root}>
      <SearchControls />
      <SearchContent isLoading={isLoading} />
    </Box>
  );
};
